/**
 * Utility functions for generating contextual tooltips for toolbar actions.
 * Tooltips indicate when context will be prompted based on current selection state.
 */

import type { ContextRequirements } from '../types/contextTypes';

/**
 * Current context state for tooltip generation
 */
export type ContextState = {
  hasConnection: boolean;
  hasDatabase: boolean;
};

/**
 * Action-specific tooltip configuration
 */
type TooltipConfig = {
  baseText: string;
  requirements: ContextRequirements;
};

/**
 * Generates contextual tooltip text based on current selection state.
 * When context is missing, indicates that it will be prompted.
 *
 * @param config - The tooltip configuration with base text and requirements
 * @param state - Current context state (whether connection/database are selected)
 * @returns Tooltip text, with prompt hint if context will be requested
 */
export function getContextualTooltip(
  config: TooltipConfig,
  state: ContextState
): string {
  const { baseText, requirements } = config;

  // Determine what's missing
  const needsConnection = requirements.requireConnection && !state.hasConnection;
  const needsDatabase = requirements.requireDatabase && !state.hasDatabase;

  // If nothing is missing, return base text
  if (!needsConnection && !needsDatabase) {
    return baseText;
  }

  // Build context prompt hint
  if (needsConnection && needsDatabase) {
    return `${baseText} (will prompt for connection and database)`;
  }

  if (needsConnection) {
    return `${baseText} (will prompt for connection)`;
  }

  if (needsDatabase) {
    return `${baseText} (will prompt for database)`;
  }

  return baseText;
}

/**
 * Pre-configured tooltip generators for common toolbar actions
 */
export const toolbarTooltips = {
  newQuery: (state: ContextState) =>
    getContextualTooltip(
      { baseText: 'Create a new query', requirements: { requireConnection: true, requireDatabase: false } },
      state
    ),

  tableExplorer: (state: ContextState) =>
    getContextualTooltip(
      { baseText: 'Open Table Explorer', requirements: { requireConnection: true, requireDatabase: true } },
      state
    ),

  tableDesigner: (state: ContextState) =>
    getContextualTooltip(
      { baseText: 'Open Table Designer', requirements: { requireConnection: true, requireDatabase: true } },
      state
    ),

  schemaOverview: (state: ContextState) =>
    getContextualTooltip(
      { baseText: 'Open Schema Overview', requirements: { requireConnection: true, requireDatabase: true } },
      state
    ),
};
